import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { access, users } from '../../services/api';

/*
 * Registo de entrada ao balcão. Era o gesto que faltava: a API existe desde o Bloco de acessos
 * (POST /api/access/gym-entry, `method: Manual`), mas não havia ecrã nenhum que a chamasse — o botão
 * "Registar entrada" da Operação/Receção não fazia nada.
 *
 * O fluxo é o da especificação de acessos: procura-se o membro, regista-se a entrada e mostra-se a
 * decisão. Se a decisão for RECUSADA, o staff (Admin) pode autorizar na mesma — e aí o motivo é
 * obrigatório e fica registado (POST /api/access/manual-override).
 */
export default function ManualEntryDialog({ open, onClose, onDone }) {
  const [all, setAll] = useState([]);
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [outcome, setOutcome] = useState(null);   // { result, reason, duplicate }
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setOutcome(null); setNote(''); setTerm(''); setSelected(null); setLoadError(false);
    users.getAll()
      .then((r) => { if (alive) setAll(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [open]);

  const matches = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (q.length < 2) return [];
    return all
      .filter((u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [term, all]);

  if (!open) return null;

  const registerEntry = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await access.gymEntry({ userId: selected.id, method: 'Manual', source: 'Recepção' });
      setOutcome(res.data);
      if (res.data?.result === 'AUTHORIZED') toast.success('Entrada registada');
      else toast.warning(`Entrada recusada: ${res.data?.reason || 'sem motivo indicado'}`);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível registar a entrada');
    } finally {
      setBusy(false);
    }
  };

  const authorizeAnyway = async () => {
    if (note.trim().length < 5) { toast.warning('Escreva o motivo (mínimo 5 caracteres)'); return; }
    setBusy(true);
    try {
      await access.manualOverride({ accessType: 'GymEntry', userId: selected.id, note: note.trim() });
      toast.success('Entrada autorizada e registada');
      setOutcome(null); setNote('');
      if (onDone) onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível autorizar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.backdrop} role="dialog" aria-modal="true" aria-label="Registar entrada">
      <div style={S.panel}>
        <div style={S.head}>
          <h3 style={S.title}>Registar entrada</h3>
          <button type="button" style={S.close} onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        {!outcome && (
          <>
            <label style={S.label} htmlFor="me-search">Membro</label>
            <input
              id="me-search"
              style={S.input}
              value={term}
              onChange={(e) => { setTerm(e.target.value); setSelected(null); }}
              placeholder="Nome ou email (mínimo 2 letras)"
              autoFocus
            />
            {loadError && <div style={S.error}>Não foi possível carregar a lista de membros.</div>}
            {!loadError && term.trim().length >= 2 && matches.length === 0 && <div style={S.hint}>Sem resultados.</div>}
            {matches.length > 0 && (
              <ul style={S.list}>
                {matches.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      style={{ ...S.option, ...(selected?.id === u.id ? S.optionActive : {}) }}
                      onClick={() => setSelected(u)}
                    >
                      <span style={S.optionName}>{u.name || '(sem nome)'}</span>
                      <span style={S.optionMeta}>{u.email}{u.isActive === false ? ' · inativo' : ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div style={S.footer}>
              <button type="button" style={S.secondary} onClick={onClose}>Cancelar</button>
              <button type="button" style={S.primary} disabled={!selected || busy} onClick={registerEntry}>
                {busy ? 'A registar…' : 'Registar entrada'}
              </button>
            </div>
          </>
        )}

        {outcome && (
          <>
            <div style={outcome.result === 'AUTHORIZED' ? S.ok : S.denied}>
              <strong>{outcome.result === 'AUTHORIZED' ? 'Entrada autorizada' : 'Entrada recusada'}</strong>
              {outcome.reason && <div style={S.reason}>Motivo: {outcome.reason}</div>}
              {outcome.duplicate && <div style={S.reason}>Já havia um registo idêntico (não se duplicou).</div>}
            </div>
            {outcome.result !== 'AUTHORIZED' && (
              <>
                <label style={S.label} htmlFor="me-note">Autorizar na mesma — motivo (obrigatório, fica registado)</label>
                <textarea id="me-note" style={S.textarea} rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                <div style={S.footer}>
                  <button type="button" style={S.secondary} onClick={() => setOutcome(null)}>Voltar</button>
                  <button type="button" style={S.primary} disabled={busy} onClick={authorizeAnyway}>
                    {busy ? 'A autorizar…' : 'Autorizar e registar'}
                  </button>
                </div>
              </>
            )}
            {outcome.result === 'AUTHORIZED' && (
              <div style={S.footer}>
                <button type="button" style={S.primary} onClick={onClose}>Fechar</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  panel: { background: 'var(--bg-card)', color: 'var(--text-primary)', borderRadius: 'var(--radius-lg, 14px)', padding: 20, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg, 0 20px 40px rgba(0,0,0,.2))' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 700 },
  close: { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text-secondary)' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)', fontSize: 14 },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)', fontSize: 14 },
  list: { listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  option: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer' },
  optionActive: { borderColor: 'var(--accent-primary)', background: 'var(--bg-hover)' },
  optionName: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  optionMeta: { fontSize: 12, color: 'var(--text-secondary)' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  primary: { padding: '10px 14px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--accent-primary)', color: 'var(--text-inverse)', fontSize: 14, cursor: 'pointer' },
  secondary: { padding: '10px 14px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  hint: { fontSize: 13, color: 'var(--text-muted)', marginTop: 8 },
  error: { fontSize: 13, color: 'var(--error-text)', marginTop: 8 },
  ok: { border: '1px solid var(--success)', background: 'var(--success-bg)', color: 'var(--success-text)', borderRadius: 'var(--radius-md, 8px)', padding: 12, fontSize: 14 },
  denied: { border: '1px solid var(--warning)', background: 'var(--warning-bg)', color: 'var(--warning-text)', borderRadius: 'var(--radius-md, 8px)', padding: 12, fontSize: 14 },
  reason: { fontSize: 13, marginTop: 4 },
};
